import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AnalyticsProvider } from '../context/AnalyticsContext';
import { AudienceProvider } from '../context/AudienceContext';
import { EngagementProvider } from '../context/EngagementContext';
import { InstagramProvider } from '../context/InstagramContext';
import { FacebookProvider } from '../context/FacebookContext';
import { LinkedInProvider } from '../context/LinkedInContext';
import { TwitterProvider } from '../context/TwitterContext';
import { AIPredictionsProvider } from '../context/AIPredictionsContext';
import { AIRecommendationsProvider } from '../context/AIRecommendationsContext';
import { TikTokProvider } from '../context/TikTokContext';
import { SocketProvider } from '../context/SocketContext';
import SocketNotificationModal from './ui/SocketNotificationModal';
import Header from './Header';
import Sidebar from './Sidebar';
import Footer from './Footer';
import Dashboard from './Dashboard';
import SEO from './SEO';
import { seoConfig } from '../utils/seoData';
import AnalyticsPage from './analytics/AnalyticsPage';
import AudiencePage from './audience/AudiencePage';
import EngagementPage from './engagement/EngagementPage';
import InstagramPage from './social/InstagramPage';
import FacebookPage from './social/FacebookPage';
import TwitterPage from './social/TwitterPage';
import LinkedInPage from './social/LinkedInPage';
import TikTokPage from './social/TikTokPage';
import AIPredictionsPage from './ai-tools/AIPredictionsPage';
import AIRecommendationsPage from './ai-tools/AIRecommendationsPage';
import RecommendationDetailPage from './ai-tools/AIRecommendationDetailPage';
import IntegrationsPage from './integrations/IntegrationsPage';
import WebTrafficPage from './web-site/WebTrafficPage';
import WebEngagementPage from './web-site/WebEngagementPage';
import WebPagesPage from './web-site/WebPagesPage';
import WebEventsPage from './web-site/WebEventsPage';
import WebChannelsPage from './web-site/WebChannelsPage';
import WebSocialToSitePage from './web-site/WebSocialToSitePage';
import WebAttributionPage from './web-site/WebAttributionPage';
import AdsOverviewPage from './ads/AdsOverviewPage';
import AdsToSitePage from './ads/AdsToSitePage';
import AdsFunnelPage from './ads/AdsFunnelPage';
import AdsConversionsPage from './ads/AdsConversionsPage';

type SectionKey = 'social-media' | 'web-site' | 'ads';

const resolveSection = (pathname: string): SectionKey => {
  // Remove language prefix for section detection
  const pathWithoutLang = pathname.replace(/^\/(tr|en)/, '');
  if (pathWithoutLang.startsWith('/web-site')) return 'web-site';
  if (pathWithoutLang.startsWith('/ads')) return 'ads';
  return 'social-media';
};

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'tr';
  const section = resolveSection(location.pathname);
  const baseRedirect = section === 'social-media'
    ? `/${currentLang}/social-media`
    : section === 'web-site'
      ? `/${currentLang}/web-site`
      : `/${currentLang}/ads`;

  // Get SEO config based on current route
  const seoData = useMemo(() => {
    const path = location.pathname;
    
    // Dashboard pages
    if (path.includes('/social-media') && !path.includes('/social-media/')) {
      return seoConfig.dashboard;
    }
    if (path.includes('/analytics')) return seoConfig.analytics;
    if (path.includes('/audience')) return seoConfig.audience;
    if (path.includes('/engagement')) return seoConfig.engagement;
    if (path.includes('/instagram')) return seoConfig.instagram;
    if (path.includes('/facebook')) return seoConfig.facebook;
    if (path.includes('/twitter')) return seoConfig.twitter;
    if (path.includes('/linkedin')) return seoConfig.linkedin;
    if (path.includes('/tiktok')) return seoConfig.tiktok;
    if (path.includes('/ai-predictions')) return seoConfig.aiPredictions;
    if (path.includes('/ai-recommendations')) return seoConfig.aiRecommendations;
    if (path.includes('/web-site/traffic')) return seoConfig.webTraffic;
    if (path.includes('/web-site/engagement')) return seoConfig.webEngagement;
    if (path.includes('/ads/overview')) return seoConfig.adsOverview;
    if (path.includes('/ads/conversions')) return seoConfig.adsConversions;
    if (path.includes('/integrations')) return seoConfig.integrations;
    
    return seoConfig.dashboard;
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Dashboard içindeki sayfa değişikliklerinde scroll to top
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    };

    const timeoutId = setTimeout(scrollToTop, 100);
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);

  return (
    <SocketProvider>
      <SEO {...seoData} />
      <div className="bg-gray-900 text-white min-h-screen">
        <Header onToggleSidebar={toggleSidebar} />
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
      
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      <main 
        id="main-content" 
        className={`pt-24 p-6 min-h-screen transition-all duration-300 ease-in-out ${
          sidebarOpen 
            ? 'ml-0 md:ml-64' 
            : 'ml-0 md:ml-10'
        }`}
      >
        <Routes>
          <Route
            index
            element={
              section === 'social-media'
                ? <Dashboard />
                : section === 'web-site'
                  ? <Navigate to={`/${currentLang}/web-site/traffic`} replace />
                  : <AdsOverviewPage />
            }
          />

          {section === 'web-site' && (
            <>
              <Route path="traffic" element={<WebTrafficPage />} />
              <Route path="engagement" element={<WebEngagementPage />} />
              <Route path="pages" element={<WebPagesPage />} />
              <Route path="events" element={<WebEventsPage />} />
              <Route path="channels" element={<WebChannelsPage />} />
              <Route path="social-to-site" element={<WebSocialToSitePage />} />
              <Route path="attribution" element={<WebAttributionPage />} />
            </>
          )}

          <Route
            path="analytics"
            element={
              <AnalyticsProvider>
                <AnalyticsPage />
              </AnalyticsProvider>
            }
          />
          <Route
            path="audience"
            element={
              <AnalyticsProvider>
                <AudienceProvider>
                  <AudiencePage />
                </AudienceProvider>
              </AnalyticsProvider>
            }
          />
          <Route
            path="engagement"
            element={
              <AnalyticsProvider>
                <EngagementProvider>
                  <EngagementPage />
                </EngagementProvider>
              </AnalyticsProvider>
            }
          />

          {section === 'social-media' && (
            <>
              <Route
                path="instagram"
                element={
                  <InstagramProvider>
                    <InstagramPage />
                  </InstagramProvider>
                }
              />
              <Route
                path="facebook"
                element={
                  <FacebookProvider>
                    <FacebookPage />
                  </FacebookProvider>
                }
              />
              <Route
                path="twitter"
                element={
                  <TwitterProvider>
                    <TwitterPage />
                  </TwitterProvider>
                }
              />
              <Route
                path="linkedin"
                element={
                  <LinkedInProvider>
                    <LinkedInPage />
                  </LinkedInProvider>
                }
              />
              <Route
                path="tiktok"
                element={
                  <TikTokProvider>
                    <TikTokPage />
                  </TikTokProvider>
                }
              />
            </>
          )}

          {section === 'ads' && (
            <>
              <Route path="overview" element={<AdsOverviewPage />} />
              <Route path="ads-to-site" element={<AdsToSitePage />} />
              <Route path="funnel" element={<AdsFunnelPage />} />
              <Route path="conversions" element={<AdsConversionsPage />} />
            </>
          )}

          <Route
            path="ai-predictions"
            element={
              <AIPredictionsProvider>
                <AIPredictionsPage />
              </AIPredictionsProvider>
            }
          />
          <Route
            path="ai-recommendations"
            element={
              <AIPredictionsProvider>
                <AIRecommendationsProvider>
                  <AIRecommendationsPage />
                </AIRecommendationsProvider>
              </AIPredictionsProvider>
            }
          />
          <Route
            path="ai-recommendations/:id"
            element={
              <AIRecommendationsProvider>
                <RecommendationDetailPage />
              </AIRecommendationsProvider>
            }
          />
          <Route
            path="recommendation-detail"
            element={
              <AIPredictionsProvider>
                <AIRecommendationsProvider>
                  <RecommendationDetailPage />
                </AIRecommendationsProvider>
              </AIPredictionsProvider>
            }
          />

          <Route path="integrations" element={<IntegrationsPage />} />

          <Route path="*" element={<Navigate to={baseRedirect} replace />} />
        </Routes>
      </main>
      <Footer />
      <SocketNotificationModal />
      </div>
    </SocketProvider>
  );
};

export default DashboardLayout;
